import { Select, type ThemeConfig } from 'antd';
import {
  createContext,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useState,
} from 'react';
import type {
  AppearancePreference,
  AppearancePreset,
} from '../services/ports/appearancePreference';

export const PURPLE_THEME: ThemeConfig = {
  token: {
    colorPrimary: '#7c3aed',
    colorInfo: '#7c3aed',
    borderRadius: 6,
    fontSize: 13,
    controlHeight: 32,
    fontFamily: 'AlibabaSans, sans-serif',
  },
};

export const MINIMAL_THEME: ThemeConfig = {
  token: {
    colorPrimary: '#202020',
    colorPrimaryBg: '#f3f3f3',
    colorPrimaryBgHover: '#e8e8e8',
    colorPrimaryBorder: '#bfbfbf',
    colorPrimaryBorderHover: '#8c8c8c',
    colorPrimaryHover: '#404040',
    colorPrimaryActive: '#000000',
    controlItemBgActive: '#ebebeb',
    controlItemBgActiveHover: '#e0e0e0',
    controlItemBgHover: '#f5f5f5',
    colorLink: '#404040',
    colorLinkHover: '#141414',
    colorLinkActive: '#000000',
    colorInfo: '#1677ff',
    colorText: '#262626',
    colorTextSecondary: '#666666',
    colorTextPlaceholder: '#6b6b6b',
    colorTextDescription: '#6b6b6b',
    colorBgLayout: '#f7f7f7',
    colorBorder: '#d9d9d9',
    colorBorderSecondary: '#e8e8e8',
    borderRadius: 6,
    fontSize: 13,
    controlHeight: 32,
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif',
    fontWeightStrong: 600,
  },
  components: {
    Button: { primaryShadow: 'none' },
    Table: { headerBg: '#f5f5f5', headerColor: '#525252' },
    Select: {
      optionSelectedBg: '#ebebeb',
      optionSelectedColor: '#202020',
      optionSelectedFontWeight: 500,
      optionActiveBg: '#f5f5f5',
    },
  },
};

export const WARM_GOLD_THEME: ThemeConfig = {
  ...MINIMAL_THEME,
  token: {
    ...MINIMAL_THEME.token,
    colorPrimary: '#005ce6',
    colorPrimaryBg: '#fff5d1',
    colorPrimaryBgHover: '#ffedab',
    colorPrimaryBorder: '#e8ca73',
    colorPrimaryBorderHover: '#d9b64a',
    colorPrimaryHover: '#004bbd',
    colorPrimaryActive: '#003c96',
    controlItemBgActive: '#fff5d1',
    controlItemBgActiveHover: '#ffedab',
    controlItemBgHover: '#f3f6fa',
    colorLink: '#005ce6',
    colorLinkHover: '#004bbd',
    colorLinkActive: '#003c96',
    colorInfo: '#005ce6',
    colorText: '#142746',
    colorTextSecondary: '#50627b',
    colorTextPlaceholder: '#5f7087',
    colorTextDescription: '#5f7087',
    colorBgLayout: '#f8fafc',
    colorBgContainer: '#ffffff',
    colorBorder: '#c4d0df',
    colorBorderSecondary: '#dce4ef',
    borderRadius: 4,
    fontWeightStrong: 500,
  },
  components: {
    ...MINIMAL_THEME.components,
    Table: { headerBg: '#eef3f8', headerColor: '#142746' },
    Select: {
      ...MINIMAL_THEME.components?.Select,
      optionSelectedBg: '#fff5d1',
      optionSelectedColor: '#142746',
      optionActiveBg: '#fff5d1',
    },
  },
};

export const CLASSIC_DESKTOP_THEME: ThemeConfig = {
  ...MINIMAL_THEME,
  token: {
    ...MINIMAL_THEME.token,
    colorPrimary: '#155da6',
    colorPrimaryBg: '#c9e1f5',
    colorPrimaryBgHover: '#bbd8ef',
    colorPrimaryBorder: '#7c9fbe',
    colorPrimaryBorderHover: '#527fa8',
    colorPrimaryHover: '#0d4d8d',
    colorPrimaryActive: '#083e74',
    controlItemBgActive: '#c9e1f5',
    controlItemBgActiveHover: '#bbd8ef',
    controlItemBgHover: '#e6edf4',
    colorLink: '#155da6',
    colorLinkHover: '#0d4d8d',
    colorLinkActive: '#083e74',
    colorInfo: '#155da6',
    colorText: '#152338',
    colorTextSecondary: '#4c5b6c',
    colorTextPlaceholder: '#586675',
    colorTextDescription: '#586675',
    colorBgLayout: '#d8dde4',
    colorBgContainer: '#ffffff',
    colorBorder: '#9aa8b8',
    colorBorderSecondary: '#c0cad5',
    borderRadius: 1,
    fontWeightStrong: 500,
  },
  components: {
    ...MINIMAL_THEME.components,
    Table: { headerBg: '#dce2e9', headerColor: '#152338' },
    Select: {
      ...MINIMAL_THEME.components?.Select,
      optionSelectedBg: '#c9e1f5',
      optionSelectedColor: '#152338',
      optionActiveBg: '#e6edf4',
    },
  },
};

export const PEARL_GLASS_THEME: ThemeConfig = {
  ...MINIMAL_THEME,
  token: {
    ...MINIMAL_THEME.token,
    colorPrimary: '#7251cf',
    colorPrimaryBg: '#eee9fb',
    colorPrimaryBgHover: '#e5dcf8',
    colorPrimaryBorder: '#c7b8ed',
    colorPrimaryBorderHover: '#a98de0',
    colorPrimaryHover: '#5d3cb8',
    colorPrimaryActive: '#493096',
    controlItemBgActive: '#eee9fb',
    controlItemBgActiveHover: '#e5dcf8',
    controlItemBgHover: '#f0f5fc',
    colorLink: '#7251cf',
    colorLinkHover: '#5d3cb8',
    colorLinkActive: '#493096',
    colorInfo: '#7251cf',
    colorText: '#26324b',
    colorTextSecondary: '#53617a',
    colorTextPlaceholder: '#5e6d85',
    colorTextDescription: '#5e6d85',
    colorBgLayout: '#edf3fb',
    colorBgContainer: '#ffffff',
    colorBorder: '#dbe3f1',
    colorBorderSecondary: '#e6ebf4',
    borderRadius: 8,
    fontWeightStrong: 500,
  },
  components: {
    ...MINIMAL_THEME.components,
    Table: { headerBg: '#f0f5fc', headerColor: '#26324b' },
    Select: {
      ...MINIMAL_THEME.components?.Select,
      optionSelectedBg: '#eee9fb',
      optionSelectedColor: '#26324b',
      optionActiveBg: '#f0f5fc',
    },
  },
};

const APPEARANCE_THEMES: Record<AppearancePreset, ThemeConfig> = {
  minimal: MINIMAL_THEME,
  purple: PURPLE_THEME,
  'warm-gold': WARM_GOLD_THEME,
  'classic-desktop': CLASSIC_DESKTOP_THEME,
  'pearl-glass': PEARL_GLASS_THEME,
};

export const APPEARANCE_OPTIONS: { value: AppearancePreset; label: string }[] =
  [
    { value: 'minimal', label: '黑白灰 · 极简' },
    { value: 'purple', label: '经典紫色' },
    { value: 'warm-gold', label: '暖金 · 极简' },
    { value: 'classic-desktop', label: '经典 · 桌面' },
    { value: 'pearl-glass', label: '珠光 · 轻透' },
  ];

interface AppearanceContextValue {
  preset: AppearancePreset;
  selectPreset(preset: AppearancePreset): void;
  saved: boolean;
}

const AppearanceContext = createContext<AppearanceContextValue | null>(null);

export function useAppearancePreset(): AppearancePreset {
  return useContext(AppearanceContext)?.preset ?? 'minimal';
}

export function useAppearanceTheme(): ThemeConfig {
  return APPEARANCE_THEMES[useAppearancePreset()];
}

export function AppearanceProvider({
  preference,
  children,
}: {
  preference: AppearancePreference;
  children: ReactNode;
}) {
  const [preset, setPreset] = useState(() => preference.read());
  const [saved, setSaved] = useState(true);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const previous = root.getAttribute('data-cpq-appearance');
    const hadAppClass = root.classList.contains('quotation-system-app');
    root.classList.add('quotation-system-app');
    root.setAttribute('data-cpq-appearance', preset);
    return () => {
      if (!hadAppClass) root.classList.remove('quotation-system-app');
      if (previous === null) root.removeAttribute('data-cpq-appearance');
      else root.setAttribute('data-cpq-appearance', previous);
    };
  }, [preset]);

  return (
    <AppearanceContext.Provider
      value={{
        preset,
        saved,
        selectPreset: (next) => {
          setPreset(next);
          setSaved(preference.write(next));
        },
      }}
    >
      {children}
    </AppearanceContext.Provider>
  );
}

export function AppearanceSwitcher() {
  const appearance = useContext(AppearanceContext);
  if (!appearance) return null;

  return (
    <>
      <Select
        aria-label="界面风格"
        title="界面风格，仅保存在当前浏览器"
        value={appearance.preset}
        options={APPEARANCE_OPTIONS}
        onChange={appearance.selectPreset}
        style={{ width: 146 }}
      />
      {!appearance.saved && <span role="status">外观仅在本次会话生效</span>}
    </>
  );
}
